/**
 * Plan operations — execute, reschedule, query, and delete plans.
 *
 * Follows the same patterns as taskOps/reminderOps:
 * - All operations return OpResult
 * - No throws (except unexpected DB errors)
 * - Uses getDb()/getNow()/callUndo() from runtime DI
 */

import { ObjectStatus, StartAtType, ReminderKind } from "./db-types.js"
import { deriveChildId, parseDbDate } from "./utils.js"
import type { DbObject } from "./db-types.js"
import { getDb, getNow, callUndo } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult } from "./op-types.js"
import { addTask } from "./taskOps.js"
import { scheduleTask } from "./scheduleOps.js"
import { addReminder, updateReminder } from "./reminderOps.js"
import { permanentlyDeleteTask } from "./taskOps.js"
import { addChecklistItem } from "./checklistOps.js"
import { computeStepDate, computeReminderDate } from "./helpers/offsetParser.js"
import {
  AnchorDateType,
  PlanInstanceStatus,
} from "./planTypes.js"
import type {
  DbPack,
  DbPlanInstance,
  PlanStepInstance,
} from "./planTypes.js"

// ---------------------------------------------------------------------------
// Template interpolation
// ---------------------------------------------------------------------------

/**
 * Replace {{key}} placeholders with values from answers.
 * Unknown keys are preserved as-is.
 */
export function interpolate(
  template: string,
  answers: Record<string, unknown>,
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
    const value = answers[key]
    return value != null ? String(value) : match
  })
}

// ---------------------------------------------------------------------------
// Condition evaluation (safe — no eval)
// ---------------------------------------------------------------------------

/**
 * Evaluate a simple condition string against answers.
 *
 * Supports:
 *   "answers.key === value"
 *   "answers.key !== value"
 *   "answers.key === true"
 *   "answers.key === false"
 *
 * Returns true if condition is undefined/empty (no condition = always active).
 */
export function evaluateCondition(
  condition: string | undefined,
  answers: Record<string, unknown>,
): boolean {
  if (!condition) return true

  // Match: answers.KEY OP VALUE
  const m = condition.match(
    /^answers\.(\w+)\s*(===|!==)\s*(.+)$/,
  )
  if (!m) return true // Unparseable → include step (safe default)

  const [, key, op, rawValue] = m
  const actual = answers[key!]

  // Parse the right-hand value
  let expected: unknown
  const trimmed = rawValue!.trim()
  if (trimmed === "true") expected = true
  else if (trimmed === "false") expected = false
  else if (trimmed.startsWith("'") && trimmed.endsWith("'"))
    expected = trimmed.slice(1, -1)
  else if (trimmed.startsWith('"') && trimmed.endsWith('"'))
    expected = trimmed.slice(1, -1)
  else if (!isNaN(Number(trimmed))) expected = Number(trimmed)
  else expected = trimmed

  if (op === "===") return actual === expected
  if (op === "!==") return actual !== expected
  return true
}

// ---------------------------------------------------------------------------
// ExecutePlan options
// ---------------------------------------------------------------------------

export interface ExecutePlanOpts {
  /** The anchor task ID. */
  anchorTaskId: string
  /** Anchor date as UTC ms number or ms-timestamp string. */
  anchorDate: string | number
  /** How steps are computed relative to anchor. */
  anchorDateType: AnchorDateType
  /** The pack template to execute. */
  pack: DbPack
  /** User answers to pack questions. */
  answers?: Record<string, unknown>
  /** Where plan tasks are created. Falls back to anchor task's parentPath. */
  parentPath?: string
  /**
   * Derive every id from this seed instead of random ones, which makes the call resumable: repeating
   * it with the same seed after an interruption creates only what is missing (the MCP idempotency
   * ledger passes the id it derived for the request). Also makes it refuse an anchor that already
   * has a plan. Leave undefined in the app.
   */
  idSeed?: string
}

// ---------------------------------------------------------------------------
// Plan instance lookup
// ---------------------------------------------------------------------------

/**
 * The plan instance of an anchor task: the live one, else (`includeDeleted`) the newest deleted one.
 * Undefined when the task never was a plan anchor. An anchor can hold several live instances: a
 * finished plan stays as history when a new one is executed on the anchor (executePlan); the
 * newest is the plan the anchor is in now.
 */
export async function getPlanInstance(
  anchorTaskId: string,
  includeDeleted = false,
): Promise<DbPlanInstance | undefined> {
  const db = getDb()
  const instances = (await db.plan.where("anchorTaskId").equals(anchorTaskId).toArray()).filter(
    (p): p is DbPlanInstance => p.kind === "instance",
  )
  const newestFirst = (a: DbPlanInstance, b: DbPlanInstance) =>
    (parseDbDate(b.createdAt) ?? 0) - (parseDbDate(a.createdAt) ?? 0)
  // An active plan first (a finished one kept as history is only "the" plan when it is the only one)
  const live = instances
    .filter((p) => !p.isDeleted)
    .sort((a, b) => Number(b.status === PlanInstanceStatus.Active) - Number(a.status === PlanInstanceStatus.Active) || newestFirst(a, b))[0]
  if (live || !includeDeleted) return live
  return instances.sort((a, b) => (parseDbDate(b.deletedAt) ?? 0) - (parseDbDate(a.deletedAt) ?? 0))[0]
}

/**
 * Is the plan finished: marked completed, or nothing in it is left to do (every step task completed,
 * canceled or in the trash)? A finished plan no longer blocks its anchor: executePlan may put a new
 * plan on it, and the finished one stays as history.
 */
export async function isPlanFinished(instance: DbPlanInstance): Promise<boolean> {
  if (instance.status === PlanInstanceStatus.Completed) return true
  const tasks = await getDb().totodo.where("anchorTaskId").equals(instance.anchorTaskId).toArray()
  return !tasks.some(
    (t) => t.planId === instance.id && t.isDeleted !== 1 && t.status === ObjectStatus.Unfinished,
  )
}

/** The id executePlan gives the plan instance of the call whose seed is `seed` (a retry finds it by this). */
export function planInstanceIdForSeed(seed: string): Promise<string> {
  return deriveChildId(seed, "plan")
}

// ---------------------------------------------------------------------------
// executePlan
// ---------------------------------------------------------------------------

/**
 * Execute a pack against an anchor task, creating N tasks with computed dates.
 *
 * 1. Evaluate conditions (filter steps by answers)
 * 2. Resolve templates (interpolate {{name}}, etc.)
 * 3. Compute dates (offset rules → absolute dates)
 * 4. Save the plan instance (step metadata lives here, not on tasks) - FIRST, with the anchor's
 *    original title and schedule, so nothing created below can be an orphan nobody can find
 * 5. Create each step task (the 3 plan fields written with the row), schedule it, add its
 *    reminder and checklist
 * 6. Convert the anchor: plan name as title, scheduled at the anchor date - LAST
 * 7. Register undo
 *
 * There is no rollback (dexie-sqlite does not undo a failed transaction), so the run is built to be
 * COMPLETED by repeating it instead: with `idSeed` every id (plan, step tasks, reminders, checklist
 * rows) is derived from the seed, each step first looks for what it would create, and every write
 * after the row is "make sure": a repeat after an interruption creates only what is missing and
 * finishes the anchor change. Without a seed (the app) ids are random, as before.
 */
export async function executePlan(
  opts: ExecutePlanOpts,
): Promise<OpResult<{ planInstanceId: string; taskIds: string[] }>> {
  const db = getDb()

  try {
    const anchorTask = await db.totodo.get(opts.anchorTaskId)
    if (!anchorTask || anchorTask.isDeleted === 1) return fail("Anchor task not found")

    const answers = opts.answers ?? {}
    const anchorMs =
      typeof opts.anchorDate === "number" ? opts.anchorDate : parseDbDate(opts.anchorDate) ?? NaN

    if (!Number.isFinite(anchorMs)) return fail("Invalid anchor date")

    const seed = opts.idSeed
    const childId = (label: string): Promise<string> | string =>
      seed !== undefined ? deriveChildId(seed, label) : crypto.randomUUID()

    const planInstanceId = seed !== undefined ? await planInstanceIdForSeed(seed) : crypto.randomUUID()
    const planTable = db.plan as import("dexie").Table<DbPlanInstance, string>
    const resumed =
      seed !== undefined ? ((await planTable.get(planInstanceId)) as DbPlanInstance | undefined) : undefined

    // A plan that was deleted since cannot be resumed (its tasks are gone, its id is taken): the
    // repeat of that call is refused, not allowed to build a plan nobody can find.
    if (resumed?.isDeleted) {
      return fail(
        "The plan this call started was deleted (delete_plan) in the meantime, so it cannot be finished. " +
          "Call execute_plan again, with a different requestId if it carried one, to create a new plan.",
      )
    }

    // One unfinished plan per anchor. A repeat of an interrupted call (same seed) finds its own
    // instance. Any other live plan on the anchor means the task is already planned - unless that plan
    // is finished (nothing left open in it): then the new plan goes on the anchor and the old one
    // stays as history. (An earlier run that died keeps blocking until it is finished or delete_plan
    // clears it.)
    let superseded: DbPlanInstance | undefined
    if (seed !== undefined && !resumed) {
      const existing = await getPlanInstance(opts.anchorTaskId)
      if (existing && (await isPlanFinished(existing))) superseded = existing
      if (existing && !superseded) {
        return fail(
          `Task ${opts.anchorTaskId} already has a plan that is not finished (${existing.id}). ` +
            "Check it with get_plan_progress. Finish its tasks (a finished plan does not block a new one, and " +
            "stays as history), or delete_plan removes it - together with its tasks - so the pack can be executed again.",
        )
      }
    }

    // 1. Evaluate conditions — filter steps by answers
    const activeSteps = opts.pack.steps.filter((step) =>
      evaluateCondition(step.condition, answers),
    )

    // 2. Resolve templates
    const resolvedSteps = activeSteps.map((step) => ({
      ...step,
      title: interpolate(step.titleTemplate, answers),
      note: step.noteTemplate
        ? interpolate(step.noteTemplate, answers)
        : undefined,
    }))

    // 3. Compute dates
    const datedSteps = resolvedSteps.map((step) => ({
      ...step,
      computedDate: computeStepDate(anchorMs, step.offsetRule, opts.anchorDateType),
    }))

    const parentPath = opts.parentPath ?? anchorTask.parentPath ?? "root/#anyday"
    const stepTaskIds: string[] = []
    for (let i = 0; i < datedSteps.length; i++) stepTaskIds.push(await childId(`step:${i}`))

    const stepInstances: PlanStepInstance[] = datedSteps.map((step, i) => ({
      taskId: stepTaskIds[i]!,
      stepKey: step.stepKey,
      offsetRule: step.offsetRule,
      sequenceOrder: step.sequenceOrder,
      estimatedMinutes: step.estimatedMinutes,
      reminderOffset: step.reminderOffset,
    }))

    // 4. Save plan instance first. The anchor is untouched until step 6, so its title and schedule
    // read here are the originals (a repeat reads them from the instance it saved).
    const origScheduleEntry = await db.startAtIndex.get(`#${opts.anchorTaskId}`)
    const originalSchedule: DbPlanInstance["originalSchedule"] =
      resumed?.originalSchedule !== undefined
        ? resumed.originalSchedule
        : origScheduleEntry
          ? { startAt: origScheduleEntry.startAt, type: origScheduleEntry.type, subType: origScheduleEntry.subType }
          : null
    const originalTitle = resumed?.originalTitle ?? anchorTask.title
    if (!resumed) {
      const planInstance: DbPlanInstance = {
        id: planInstanceId,
        kind: "instance",
        anchorTaskId: opts.anchorTaskId,
        packId: opts.pack.id,
        packVersion: opts.pack.version,
        anchorDate: String(anchorMs),
        anchorDateType: opts.anchorDateType,
        status: PlanInstanceStatus.Active,
        answers: Object.keys(answers).length > 0 ? answers : undefined,
        steps: stepInstances,
        originalTitle,
        originalSchedule,
        createdAt: getNow(),
        createdBy: db.cloud.currentUserId,
        isDeleted: 0,
      }
      await planTable.add(planInstance)
      // The finished plan this one replaces stays, as history, marked completed (nothing in it was open)
      if (superseded && superseded.status !== PlanInstanceStatus.Completed) {
        await planTable.update(superseded.id, {
          status: PlanInstanceStatus.Completed,
          updatedAt: getNow(),
          updatedBy: db.cloud.currentUserId,
        })
      }
    }

    // 5. Create tasks + schedule + reminders + checklists, each step only where it is missing
    const taskIds: string[] = []
    for (let i = 0; i < datedSteps.length; i++) {
      const step = datedSteps[i]!
      const taskId = stepTaskIds[i]!

      const existing = await db.totodo.get(taskId)
      if (existing?.isDeleted === 1) continue // the user trashed it since: not brought back
      if (!existing) {
        const result = await addTask({
          id: taskId,
          title: step.title,
          note: step.note,
          parentPath,
          tags: step.tags ?? [],
          fields: {
            anchorTaskId: opts.anchorTaskId,
            anchorTaskIndex: (i + 1) * 1000,
            planId: planInstanceId,
          },
        })
        if (!result.ok) return fail(`Failed to create task for step "${step.stepKey}": ${result.error}`)
      } else if (existing.planId !== planInstanceId) {
        await db.totodo.update(taskId, {
          anchorTaskId: opts.anchorTaskId,
          anchorTaskIndex: (i + 1) * 1000,
          planId: planInstanceId,
        })
      }

      // Schedule task to computed date (moves an inbox task to Anyday)
      if (!(await db.startAtIndex.get(`#${taskId}`))) {
        const scheduled = await scheduleTask(taskId, String(step.computedDate), StartAtType.Default)
        if (!scheduled.ok) return fail(`Failed to schedule step "${step.stepKey}": ${scheduled.error}`)
      }

      // The task's path as it is now: scheduling moved an inbox task to Anyday, and a reminder or
      // checklist row filed under the old path would point at a place the task has left
      const taskPath = (await db.totodo.get(taskId))?.parentPath ?? parentPath

      // Add reminder if step has reminderOffset. NOT the app's `rem_<taskId>` scheme: partitionDuplicateReminders
      // keeps such a row and drops every other reminder of the task, so a reminder the user adds later
      // would never fire. With a seed the id is derived from it (a repeat finds the row); the app, which
      // has no seed, gets a random one.
      if (step.reminderOffset) {
        const reminderId = await childId(`step:${i}:reminder`)
        if (seed === undefined || !(await db.reminder.get(reminderId))) {
          const reminderMs = computeReminderDate(step.computedDate, step.reminderOffset)
          const reminder = await addReminder(taskId, String(reminderMs), ReminderKind.Reminder, {
            title: step.title,
            parentPath: taskPath,
            id: reminderId,
          })
          if (!reminder.ok) return fail(`Failed to add the reminder of step "${step.stepKey}": ${reminder.error}`)
        }
      }

      // Add checklist items, under the task's path as it is now
      const items = step.checklistItems ?? []
      for (let j = 0; j < items.length; j++) {
        const itemId = await childId(`step:${i}:item:${j}`)
        if (await db.totodo.get(itemId)) continue
        const item = await addChecklistItem(taskId, taskPath, items[j]!, undefined, undefined, itemId)
        if (!item.ok) return fail(`Failed to add a checklist item to step "${step.stepKey}": ${item.error}`)
      }

      taskIds.push(taskId)
    }

    // 6. Convert the anchor task, last: plan name as title + scheduled at the anchor date
    if (anchorTask.planId !== planInstanceId || anchorTask.title !== opts.pack.name) {
      await db.totodo.update(opts.anchorTaskId, {
        planId: planInstanceId,
        title: opts.pack.name,
      })
    }
    const anchorEntry = await db.startAtIndex.get(`#${opts.anchorTaskId}`)
    if (!anchorEntry || anchorEntry.startAt !== String(anchorMs) || anchorEntry.type !== StartAtType.Default) {
      const scheduled = await scheduleTask(opts.anchorTaskId, String(anchorMs), StartAtType.Default)
      if (!scheduled.ok) return fail(`Failed to schedule the anchor task: ${scheduled.error}`)
    }

    // 7. Register undo — deletes all plan tasks + plan instance, restores anchor
    callUndo(
      `Plan: ${opts.pack.name}`,
      async () => {
        await db.plan.delete(planInstanceId)
        await db.totodo.update(opts.anchorTaskId, {
          planId: undefined,
          title: originalTitle,
        })
        // Restore original schedule
        if (origScheduleEntry) {
          await scheduleTask(
            opts.anchorTaskId,
            origScheduleEntry.startAt,
            origScheduleEntry.type ?? StartAtType.Default,
          )
        }
        for (const id of taskIds) {
          await permanentlyDeleteTask(id)
        }
      },
      async () => {
        // Redo not supported for plan execution
      },
    )

    return ok({ planInstanceId, taskIds })
  } catch (e) {
    return fail(`executePlan failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// reschedulePlan
// ---------------------------------------------------------------------------

/**
 * Reschedule all plan tasks when the anchor date changes.
 * Uses plan instance as single source of truth for offset rules.
 * Also cascades reminder updates, and moves the anchor task itself to the new date.
 * Repeating it after an interruption finishes the job (every write sets a value derived from the
 * new anchor date).
 */
export async function reschedulePlan(
  anchorTaskId: string,
  newAnchorDate: string | number,
): Promise<OpResult<{ updated: number; anchorMoved: boolean }>> {
  const db = getDb()

  try {
    const anchorMs =
      typeof newAnchorDate === "number" ? newAnchorDate : parseDbDate(newAnchorDate) ?? NaN

    if (!Number.isFinite(anchorMs)) return fail("Invalid anchor date")

    // 1. Find plan instance
    const planInstance = await getPlanInstance(anchorTaskId)

    if (!planInstance) return fail("No active plan instance found")

    // Build step lookup: taskId → step metadata
    const stepByTaskId = new Map(
      planInstance.steps.map((s) => [s.taskId, s]),
    )

    // 2. Get all plan tasks
    const planTasks = await db.totodo
      .where("anchorTaskId")
      .equals(anchorTaskId)
      .toArray()

    let updated = 0

    for (const task of planTasks) {
      if (task.isDeleted === 1) continue
      const stepInfo = stepByTaskId.get(task.id!)
      if (!stepInfo) continue

      // 3. Recompute task date
      const newStepMs = computeStepDate(
        anchorMs,
        stepInfo.offsetRule,
        planInstance.anchorDateType,
      )
      const scheduled = await scheduleTask(task.id!, String(newStepMs), StartAtType.Default)
      if (!scheduled.ok) return fail(scheduled.error)

      // 4. Cascade reminder updates (and the path the task has now: scheduling may have moved it
      // out of the inbox)
      const taskPath = (await db.totodo.get(task.id!))?.parentPath
      const reminders = await db.reminder
        .where("taskId")
        .equals(task.id!)
        .filter((r) => !r.isDeleted)
        .toArray()

      for (const reminder of reminders) {
        const fireAt = stepInfo.reminderOffset
          ? String(computeReminderDate(newStepMs, stepInfo.reminderOffset))
          : String(newStepMs)
        await updateReminder(reminder.id, { fireAt, ...(taskPath ? { parentPath: taskPath } : {}) })
      }

      updated++
    }

    // 5. The anchor task is part of the plan: it sits at the anchor date
    let anchorMoved = false
    const anchor = await db.totodo.get(anchorTaskId)
    if (anchor && anchor.isDeleted !== 1) {
      const scheduled = await scheduleTask(anchorTaskId, String(anchorMs), StartAtType.Default)
      if (!scheduled.ok) return fail(scheduled.error)
      anchorMoved = true
    }

    // 6. Update plan instance
    const now = getNow()
    await (db.plan as import("dexie").Table<DbPlanInstance, string>).update(planInstance.id, {
      anchorDate: String(anchorMs),
      updatedAt: now,
      updatedBy: db.cloud.currentUserId,
    })

    return ok({ updated, anchorMoved })
  } catch (e) {
    return fail(`reschedulePlan failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// getPlanTasks
// ---------------------------------------------------------------------------

/**
 * Get all tasks belonging to a plan, sorted by anchorTaskIndex.
 */
export async function getPlanTasks(
  anchorTaskId: string,
): Promise<DbObject[]> {
  const db = getDb()
  const tasks = await db.totodo
    .where("anchorTaskId")
    .equals(anchorTaskId)
    .sortBy("anchorTaskIndex")
  // An anchor that went through several plans keeps the tasks of the finished ones: only the current
  // plan's tasks (and any from before tasks carried a planId) are "its" tasks.
  const current = await getPlanInstance(anchorTaskId)
  return current ? tasks.filter((t) => !t.planId || t.planId === current.id) : tasks
}

// ---------------------------------------------------------------------------
// getPlanProgress
// ---------------------------------------------------------------------------

export interface PlanProgress {
  total: number
  completed: number
  percent: number
}

/**
 * Compute plan completion progress.
 */
export async function getPlanProgress(
  anchorTaskId: string,
): Promise<PlanProgress> {
  const tasks = await getPlanTasks(anchorTaskId)
  const completed = tasks.filter(
    (t) => t.status === ObjectStatus.Completed,
  ).length
  return {
    total: tasks.length,
    completed,
    percent: tasks.length ? Math.round((completed / tasks.length) * 100) : 0,
  }
}

// ---------------------------------------------------------------------------
// deletePlan
// ---------------------------------------------------------------------------

/**
 * Put the anchor task's schedule back as the plan found it (`null` = it had none; undefined = a plan
 * from before the schedule was recorded: left as it is). Written straight to startAtIndex, not
 * through scheduleTask, so deleting a plan registers no extra undo step in the app.
 */
async function restoreAnchorSchedule(
  anchorTaskId: string,
  original: DbPlanInstance["originalSchedule"],
): Promise<void> {
  if (original === undefined) return
  const db = getDb()
  const entryId = `#${anchorTaskId}`
  const existing = await db.startAtIndex.get(entryId)
  if (original === null) {
    if (existing) await db.startAtIndex.delete(entryId)
  } else if (existing) {
    await db.startAtIndex.update(entryId, { startAt: original.startAt, type: original.type, subType: original.subType })
  } else {
    const last = await db.startAtIndex.orderBy("index").last()
    await db.startAtIndex.add({
      id: entryId,
      index: last ? last.index + 1 : 0,
      startAt: original.startAt,
      type: original.type,
      subType: original.subType,
    })
  }
  await db.totodo.update(anchorTaskId, { updatedAt: getNow() })
}

/**
 * Delete the plan: permanently delete all plan tasks, put the anchor task back as it was (title,
 * and the schedule it had before the plan scheduled it) and soft-delete the plan instance.
 * The anchor task itself is preserved.
 *
 * The instance goes LAST: it holds what the anchor must be restored to, so a call that dies half
 * way is finished by repeating it. A plan that is already deleted answers ok (`deleted: 0`); a task
 * that never had a plan is an error.
 */
export async function deletePlan(
  anchorTaskId: string,
): Promise<OpResult<{ deleted: number; alreadyDeleted?: boolean }>> {
  const db = getDb()

  try {
    const planInstance = await getPlanInstance(anchorTaskId, true)
    // The tasks of this plan only: those of a finished plan kept as history stay
    const planTasks = (await db.totodo.where("anchorTaskId").equals(anchorTaskId).toArray()).filter(
      (t) => !planInstance || !t.planId || t.planId === planInstance.id,
    )

    if (!planInstance && planTasks.length === 0) return fail(`No plan found for task ${anchorTaskId}`)
    if (planInstance?.isDeleted && planTasks.length === 0) return ok({ deleted: 0, alreadyDeleted: true })

    // Delete plan tasks (uses existing permanent delete with cascade)
    for (const task of planTasks) {
      await permanentlyDeleteTask(task.id!)
    }

    // Restore the anchor task: its title, no plan, and the schedule it had before the plan
    const anchor = await db.totodo.get(anchorTaskId)
    if (anchor) {
      await db.totodo.update(anchorTaskId, {
        planId: undefined,
        ...(planInstance?.originalTitle ? { title: planInstance.originalTitle } : {}),
      })
      await restoreAnchorSchedule(anchorTaskId, planInstance?.originalSchedule)
    }

    // The instance last
    if (planInstance && !planInstance.isDeleted) {
      const now = getNow()
      await (db.plan as import("dexie").Table<DbPlanInstance, string>).update(planInstance.id, {
        status: PlanInstanceStatus.Cancelled,
        isDeleted: 1,
        deletedAt: now,
        updatedAt: now,
        updatedBy: db.cloud.currentUserId,
      })
    }

    return ok({ deleted: planTasks.length })
  } catch (e) {
    return fail(`deletePlan failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}
